import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireMicrositeAdmin } from '@/components/auth/require-microsite-admin';
import { RequireStaff } from '@/components/auth/require-staff';
import { RequireTenantAdministration } from '@/components/access/require-tenant-administration';
import { RequirePlatformPermission } from '@/components/access/require-platform-permission';
import { RequireModule, RequirePermission } from '@/components/access/require-permission';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { ChatWidget } from '@/components/chat/chat-widget';
import { AppShell } from '@/components/layout/app-shell';
import { RootRouteGate } from '@/components/routing/root-route-gate';
import { StaffLandingRoute } from '@/components/routing/staff-landing-route';
import { LegacyRedirect } from '@/components/routing/legacy-redirect';
import { RETIRED_CRM_ROUTES } from '@/pages/hubs/routes';
const load = <T extends Record<string, unknown>, K extends keyof T>(factory: () => Promise<T>, name: K) =>
  lazy(async () => ({ default: (await factory())[name] as ComponentType }));

const EntityAdministrationPage = load(() => import('@/pages/admin/entity'), 'EntityAdministrationPage');
const TenantAdministrationPage = load(() => import('@/pages/admin/tenant'), 'TenantAdministrationPage');
const PlatformAdministrationPage = load(() => import('@/pages/admin/platform'), 'PlatformAdministrationPage');
const AdminAuditLogPage = load(() => import('@/pages/admin/audit-log'), 'AdminAuditLogPage');
const AdminEventDetailPage = load(() => import('@/pages/admin/event-detail'), 'AdminEventDetailPage');
const AdminEventsPage = load(() => import('@/pages/admin/events'), 'AdminEventsPage');
const AdminCustomPagesPage = load(() => import('@/pages/admin/custom-pages'), 'AdminCustomPagesPage');
const AdminCustomPageBuilderPage = load(() => import('@/pages/admin/custom-page-builder'), 'AdminCustomPageBuilderPage');
const AdminUsersPage = load(() => import('@/pages/admin/users'), 'AdminUsersPage');
const AccessInvitationPage = load(() => import('@/pages/access/invitation-accept'), 'AccessInvitationPage');
const EntityMicrositeAdminPage = load(() => import('@/pages/admin/entity-microsite'), 'EntityMicrositeAdminPage');
const LegacyImportPage = load(() => import('@/pages/admin/legacy-import'), 'LegacyImportPage');
const LegacyDataPage = load(() => import('@/pages/admin/legacy-data'), 'LegacyDataPage');
const AccountIntelligencePage = load(() => import('@/pages/admin/account-intelligence'), 'AccountIntelligencePage');
const AdminBusinessCasesPage = load(() => import('@/pages/admin/business-cases'), 'AdminBusinessCasesPage');
const AdminQuoteDetailPage = load(() => import('@/pages/admin/quote-detail'), 'AdminQuoteDetailPage');
const AdminQuotesPage = load(() => import('@/pages/admin/quotes'), 'AdminQuotesPage');
const TendersPage = load(() => import('@/pages/tenders'), 'TendersPage');
const TenderDetailPage = load(() => import('@/pages/tenders/detail'), 'TenderDetailPage');
const SowsPage = load(() => import('@/pages/sows'), 'SowsPage');
const SowDetailPage = load(() => import('@/pages/sows/detail'), 'SowDetailPage');
const TenderOpportunitiesPage = load(() => import('@/pages/tenders/opportunities'), 'TenderOpportunitiesPage');
const TenderOpportunityDetailPage = load(() => import('@/pages/tenders/opportunities/detail'), 'TenderOpportunityDetailPage');
const XeroAdminPage = load(() => import('@/pages/admin/xero'), 'XeroAdminPage');
const AccountingPlatformPage = load(() => import('@/pages/admin/accounting-platform'), 'AccountingPlatformPage');
const XeroBankFeedPage = load(() => import('@/pages/admin/xero-bank-feed'), 'XeroBankFeedPage');
const XeroInvoiceDetailPage = load(() => import('@/pages/admin/xero-invoice-detail'), 'XeroInvoiceDetailPage');
const XeroReconciliationPage = load(() => import('@/pages/admin/xero-reconciliation'), 'XeroReconciliationPage');
const CatalogueAdminPage = load(() => import('@/pages/catalogue/admin'), 'CatalogueAdminPage');
const ContentCatalogueAdminPage = load(() => import('@/pages/content/admin'), 'ContentCatalogueAdminPage');
const KnowledgeSourcePage = load(() => import('@/pages/knowledge/source'), 'KnowledgeSourcePage');
const InsightsHubPage = load(() => import('@/pages/hubs'), 'InsightsHubPage');
const ConnectHubPage = load(() => import('@/pages/hubs'), 'ConnectHubPage');
const AccountsPage = load(() => import('@/pages/crm/accounts'), 'AccountsPage');
const AccountDetailPage = load(() => import('@/pages/crm/account-detail'), 'AccountDetailPage');
const ContactDetailPage = load(() => import('@/pages/crm/contact-detail'), 'ContactDetailPage');
const ContactsPage = load(() => import('@/pages/crm/contacts'), 'ContactsPage');
const IcloudPage = load(() => import('@/pages/crm/icloud/icloud-page'), 'IcloudPage');
const TestDataReviewPage = load(() => import('@/pages/crm/test-data-review'), 'TestDataReviewPage');
const DashboardPage = load(() => import('@/pages/dashboard'), 'DashboardPage');
const HomeOverviewPage = load(() => import('@/pages/home/overview'), 'HomeOverviewPage');
const RelationshipHealthPage = load(() => import('@/pages/relationship-health/home'), 'RelationshipHealthPage');
const WorkflowMapPage = load(() => import('@/pages/workflow-map'), 'WorkflowMapPage');
const LifecyclePage = load(() => import('@/pages/lifecycle'), 'LifecyclePage');
const DailyBriefingPage = load(() => import('@/pages/agent/briefing'), 'DailyBriefingPage');
const CapabilityMapPage = load(() => import('@/pages/capability-map'), 'CapabilityMapPage');
const GeographicWorkspacePage = load(() => import('@/pages/geography/workspace'), 'GeographicWorkspacePage');
const SalesFloorTwinPage = load(() => import('@/pages/sales-floor-twin'), 'SalesFloorTwinPage');
const OpportunityDetailPage = load(() => import('@/pages/crm/opportunity-detail'), 'OpportunityDetailPage');
const OpportunitiesPage = load(() => import('@/pages/crm/opportunities'), 'OpportunitiesPage');
const OpportunityHubPage = load(() => import('@/pages/crm/opportunity-hub'), 'OpportunityHubPage');
const EnquiriesPage = load(() => import('@/pages/crm/enquiries'), 'EnquiriesPage');
const EnquiryDetailPage = load(() => import('@/pages/crm/enquiry-detail'), 'EnquiryDetailPage');
const EventMicrositePage = load(() => import('@/pages/events/event-microsite'), 'EventMicrositePage');
const PublicEnquiryPage = load(() => import('@/pages/public/enquire'), 'PublicEnquiryPage');
const PublicBookingPage = load(() => import('@/pages/public/booking'), 'PublicBookingPage');
const PublicContentAssetPage = load(() => import('@/pages/public/content-asset'), 'PublicContentAssetPage');
const MarketingHomePage = load(() => import('@/pages/public/marketing-home'), 'MarketingHomePage');
const PublicVoicePage = load(() => import('@/pages/public/voice'), 'PublicVoicePage');
const PublicCataloguePage = load(() => import('@/pages/public/catalogue'), 'PublicCataloguePage');
const PublicQuoteViewPage = load(() => import('@/pages/public/quote-view'), 'PublicQuoteViewPage');
const PublicManagedDocumentPage = load(() => import('@/pages/public/document'), 'PublicManagedDocumentPage');
const PublicMicrositePage = load(() => import('@/pages/microsites/public-microsite'), 'PublicMicrositePage');
const PortalPage = load(() => import('@/pages/portal'), 'PortalPage');
const PartnerDetailPage = load(() => import('@/pages/partners/partner-detail'), 'PartnerDetailPage');
const PartnerOverviewPage = load(() => import('@/pages/partners/overview'), 'PartnerOverviewPage');
const PartnersPage = load(() => import('@/pages/partners/partners'), 'PartnersPage');
const ServiceCatalogueAdminPage = load(() => import('@/pages/services/admin'), 'ServiceCatalogueAdminPage');
const SettingsAppearancePage = load(() => import('@/pages/settings/appearance'), 'SettingsAppearancePage');
const SettingsGeneralPage = load(() => import('@/pages/settings/general'), 'SettingsGeneralPage');
const HomepageConfigurationPage = load(() => import('@/pages/settings/homepage'), 'HomepageConfigurationPage');
const SettingsLayout = load(() => import('@/pages/settings/layout'), 'SettingsLayout');
const ChannelConnectPage = load(() => import('@/pages/settings/channel-connect'), 'ChannelConnectPage');
const SettingsChannelsPage = load(() => import('@/pages/settings/channels'), 'SettingsChannelsPage');
const SettingsAccountingPage = load(() => import('@/pages/settings/accounting'), 'SettingsAccountingPage');
const PlatformConfigurationPage = load(() => import('@/pages/settings/platform-configuration'), 'PlatformConfigurationPage');
const CrmProgrammeOperationsPage = load(() => import('@/pages/settings/crm-programme-operations'), 'CrmProgrammeOperationsPage');
const SocialWorkspacePage = load(() => import('@/pages/social/workspace'), 'SocialWorkspacePage');
const EmailWorkspacePage = load(() => import('@/pages/email/workspace'), 'EmailWorkspacePage');
const AgentPage = load(() => import('@/pages/agent'), 'AgentPage');
const PersonalWorkPage = load(() => import('@/pages/personal-work'), 'PersonalWorkPage');
const VoiceWorkspacePage = load(() => import('@/pages/voice/workspace'), 'VoiceWorkspacePage');
const WebSignalsWorkspacePage = load(() => import('@/pages/web-signals/workspace'), 'WebSignalsWorkspacePage');
const MarketIntelligenceWorkspacePage = load(() => import('@/pages/intelligence/workspace'), 'MarketIntelligenceWorkspacePage');
const AcquisitionAnalyticsWorkspacePage = load(() => import('@/pages/analytics/workspace'), 'AcquisitionAnalyticsWorkspacePage');
const SmartOutlookPage = load(() => import('@/pages/acquisition/outlook'), 'SmartOutlookPage');
const SharedMailboxPage = load(() => import('@/pages/acquisition/shared-mailbox'), 'SharedMailboxPage');
const WhatsAppWorkspacePage = load(() => import('@/pages/whatsapp/workspace'), 'WhatsAppWorkspacePage');
const MailboxMonitoringPage = load(() => import('@/pages/acquisition/mailbox-monitoring'), 'MailboxMonitoringPage');
const ContentHubCalendarPage = load(() => import('@/pages/content-hub/core-pages'), 'ContentHubCalendarPage');
const ContentHubDashboardPage = load(() => import('@/pages/content-hub/core-pages'), 'ContentHubDashboardPage');
const ContentHubGeneratePage = load(() => import('@/pages/content-hub/core-pages'), 'ContentHubGeneratePage');
const ContentHubPostsPage = load(() => import('@/pages/content-hub/core-pages'), 'ContentHubPostsPage');
const ContentHubPostEditorPage = load(() => import('@/pages/content-hub/post-editor-page'), 'ContentHubPostEditorPage');
const ContentHubMediaPage = load(() => import('@/pages/content-hub/media-page'), 'ContentHubMediaPage');
const ContentHubKnowledgePage = load(() => import('@/pages/content-hub/knowledge-page'), 'ContentHubKnowledgePage');
const VideoStudioPage = load(() => import('@/pages/content-hub/video/studio-page'), 'VideoStudioPage');
const VideoUsagePage = load(() => import('@/pages/content-hub/video/studio-page'), 'VideoUsagePage');
const VideoProjectPage = load(() => import('@/pages/content-hub/video/project-page'), 'VideoProjectPage');
const ContentHubBookingsPage = load(() => import('@/pages/content-hub/growth-pages'), 'ContentHubBookingsPage');
const ContentHubLeadMagnetPage = load(() => import('@/pages/content-hub/growth-pages'), 'ContentHubLeadMagnetPage');
const ContentHubLeadsPage = load(() => import('@/pages/content-hub/growth-pages'), 'ContentHubLeadsPage');
const ContentHubMailingListPage = load(() => import('@/pages/content-hub/growth-pages'), 'ContentHubMailingListPage');
const ContentHubSequencesPage = load(() => import('@/pages/content-hub/growth-pages'), 'ContentHubSequencesPage');
const ContentHubAuthorityPage = load(() => import('@/pages/content-hub/operations-pages'), 'ContentHubAuthorityPage');
const ContentHubAutomationPage = load(() => import('@/pages/content-hub/operations-pages'), 'ContentHubAutomationPage');
const ContentHubEntityProfilePage = load(() => import('@/pages/content-hub/operations-pages'), 'ContentHubEntityProfilePage');
const ContentHubGoLivePage = load(() => import('@/pages/content-hub/operations-pages'), 'ContentHubGoLivePage');
const ContentHubHowItWorksPage = load(() => import('@/pages/content-hub/operations-pages'), 'ContentHubHowItWorksPage');
const ContentHubOperationsPage = load(() => import('@/pages/content-hub/operations-pages'), 'ContentHubOperationsPage');
const ContentHubIntelligencePage = load(() => import('@/pages/content-hub/intelligence-page'), 'ContentHubIntelligencePage');
const ContentHubAdvertisingPage = load(() => import('@/pages/content-hub/advertising-page'), 'ContentHubAdvertisingPage');
const ContentHubReviewRequestsPage = load(() => import('@/pages/content-hub/operations-pages'), 'ContentHubReviewRequestsPage');
const ContentHubReportsPage = load(() => import('@/pages/content-hub/reporting-page'), 'ContentHubReportsPage');
const ContentHubReportPrintPage = load(() => import('@/pages/content-hub/reporting-page'), 'ContentHubReportPrintPage');
const ResearchWorkspacePage = load(() => import('@/pages/research/workspace'), 'ResearchWorkspacePage');
const FilesWorkspacePage = load(() => import('@/pages/files/workspace'), 'FilesWorkspacePage');
const ManagedDocumentPage = load(() => import('@/pages/files/document'), 'ManagedDocumentPage');
const SharedFilePage = load(() => import('@/pages/files/shared-file'), 'SharedFilePage');
const CustomPageViewer = load(() => import('@/pages/custom-page'), 'CustomPageViewer');

function PartnerEngagementIndex() {
  const { hasModule } = useTenantEntity();
  return <Navigate to={hasModule('partner-engagement') ? '/partner-engagement/partners' : '/partner-engagement/enquiries'} replace />;
}

export function App() {
  return (
    <Suspense fallback={<div role="status" className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">Loading workspace…</div>}>
      <Routes>
      {/* Public (no app shell) — embeddable enquiry form + AI chat widget */}
      <Route
        path="enquire"
        element={
          <>
            <PublicEnquiryPage />
            <ChatWidget />
          </>
        }
      />
      <Route path="voice" element={<PublicVoicePage />} />
      <Route path="sites/:slug/book" element={<PublicBookingPage />} />
      <Route path="sites/:slug/resources/:id" element={<PublicContentAssetPage />} />

      {/* Partner Portal — signed-in external entity users, own layout (no app shell) */}
      <Route path="portal" element={<PortalPage />} />

      {/* Public event microsite — external-site look, no app shell */}
      <Route path="events/:slug" element={<EventMicrositePage />} />

      {/* Published entity microsite — anonymous branded page, no app shell */}
      <Route path="sites/:slug" element={<PublicMicrositePage />} />

      {/* Public solution catalogue + guest quote-builder store — no app shell */}
      <Route path="catalogue" element={<PublicCataloguePage />} />
      <Route path="quote/:token" element={<PublicQuoteViewPage />} />
      <Route path="public/documents/:publicId" element={<PublicManagedDocumentPage />} />
      <Route path="access/invitations/accept" element={<AccessInvitationPage />} />

      <Route element={<RootRouteGate marketingPage={<MarketingHomePage />} />}>
        <Route element={<AppShell />}>
          <Route element={<RequireStaff />}>
          <Route element={<RequirePermission permissions={['entity.manage', 'entity.records.write', 'entity.content.manage', 'entity.profile.manage', 'entity.social-connections.manage', 'entity.integrations.manage']} />}>
            <Route path="admin/entity" element={<EntityAdministrationPage />} />
          </Route>
          <Route element={<RequireTenantAdministration />}>
            <Route path="admin/tenant" element={<TenantAdministrationPage />} />
          </Route>
          <Route element={<RequirePlatformPermission />}>
            <Route path="admin/platform" element={<PlatformAdministrationPage />} />
          </Route>
          <Route path="work" element={<PersonalWorkPage />} />
          <Route index element={<StaffLandingRoute />} />
          {/* Overview */}
          <Route element={<RequireModule moduleKey="overview" />}>
            <Route path="overview" element={<HomeOverviewPage />} />
            <Route path="lifecycle" element={<LifecyclePage />} />
            <Route path="agent/briefing" element={<DailyBriefingPage />} />
          <Route element={<RequirePermission permission="entity.records.read" />}>
            <Route path="relationship-health" element={<RelationshipHealthPage />} />
          </Route>
          <Route path="next-actions/*" element={<LegacyRedirect from="/next-actions" to="/relationship-health" defaultSearch={{ view: 'actions' }} />} />
            <Route path="workflow-map" element={<WorkflowMapPage />} />
            <Route path="capability-map" element={<CapabilityMapPage />} />
            <Route element={<RequirePermission permission="entity.records.read" />}>
              <Route path="sales-floor" element={<SalesFloorTwinPage />} />
              <Route path="geography" element={<GeographicWorkspacePage />} />
            </Route>
            <Route path="dashboard" element={<DashboardPage />} />
          </Route>
          <Route path="channel-connect" element={<ChannelConnectPage />} />
          <Route path="knowledge/:id" element={<KnowledgeSourcePage />} />
          <Route path="pages/:slug" element={<CustomPageViewer />} />
          <Route element={<RequirePermission permission="entity.records.read" />}>
            <Route path="files" element={<FilesWorkspacePage />} />
            <Route path="files/shared" element={<SharedFilePage />} />
            <Route path="files/:id" element={<ManagedDocumentPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="acquisition" />}>
            <Route path="agent" element={<AgentPage />} />
            <Route path="acquisition/agent" element={<LegacyRedirect from="/acquisition/agent" to="/agent" />} />
            <Route path="acquisition/assistant" element={<LegacyRedirect from="/acquisition/assistant" to="/agent" />} />
          </Route>
          <Route element={<RequireModule moduleKey="acquisition" />}>
            <Route path="acquisition/social" element={<SocialWorkspacePage />} />
            <Route path="acquisition/email" element={<EmailWorkspacePage />} />

            <Route path="acquisition/voice" element={<VoiceWorkspacePage />} />
            <Route path="acquisition/web-signals" element={<WebSignalsWorkspacePage />} />
            <Route path="acquisition/intelligence" element={<MarketIntelligenceWorkspacePage />} />
            <Route path="acquisition/analytics" element={<AcquisitionAnalyticsWorkspacePage />} />
            <Route path="acquisition/outlook" element={<SmartOutlookPage />} />
            <Route path="acquisition/shared-mailbox" element={<SharedMailboxPage />} />
            <Route element={<RequirePermission permission="entity.records.read" />}>
              <Route path="acquisition/mailbox-monitoring" element={<MailboxMonitoringPage />} />
            </Route>
          </Route>
          <Route element={<RequireModule moduleKey="content-hub" />}>
            <Route path="content-hub" element={<ContentHubDashboardPage />} />
            <Route element={<RequirePermission permission="entity.content.manage" />}>
              <Route path="content-hub/generate" element={<ContentHubGeneratePage />} />
              <Route path="content-hub/posts" element={<ContentHubPostsPage />} />
              <Route path="content-hub/posts/new" element={<ContentHubPostEditorPage />} />
              <Route path="content-hub/posts/:id" element={<ContentHubPostEditorPage />} />
              <Route path="content-hub/media" element={<ContentHubMediaPage />} />
              <Route path="content-hub/knowledge" element={<ContentHubKnowledgePage />} />
              <Route path="content-hub/video" element={<VideoStudioPage />} />
              <Route path="content-hub/video/usage" element={<VideoUsagePage />} />
              <Route path="content-hub/video/:id" element={<VideoProjectPage />} />
              <Route path="content-hub/calendar" element={<ContentHubCalendarPage />} />
              <Route path="content-hub/intelligence" element={<ContentHubIntelligencePage />} />
              <Route path="content-hub/ads" element={<ContentHubAdvertisingPage />} />
            </Route>
            <Route path="content-hub/leads" element={<ContentHubLeadsPage />} />
            <Route path="content-hub/bookings" element={<ContentHubBookingsPage />} />
            <Route path="content-hub/sequences" element={<ContentHubSequencesPage />} />
            <Route path="content-hub/lead-magnet" element={<ContentHubLeadMagnetPage />} />
            <Route path="content-hub/mailing-list" element={<ContentHubMailingListPage />} />
            <Route path="content-hub/authority" element={<ContentHubAuthorityPage />} />
            <Route path="content-hub/operations" element={<ContentHubOperationsPage />} />
            <Route path="content-hub/brand" element={<LegacyRedirect from="/content-hub/brand" to="/content-hub/entity-profile" />} />
            <Route element={<RequirePermission permission="entity.profile.manage" />}>
              <Route path="content-hub/entity-profile" element={<ContentHubEntityProfilePage />} />
            </Route>
            <Route path="content-hub/review-requests" element={<ContentHubReviewRequestsPage />} />
            <Route path="content-hub/reports" element={<ContentHubReportsPage />} />
            <Route path="content-hub/report-print" element={<ContentHubReportPrintPage />} />
            <Route path="content-hub/automation" element={<ContentHubAutomationPage />} />
            <Route path="content-hub/go-live" element={<ContentHubGoLivePage />} />
            <Route path="content-hub/how-it-works" element={<ContentHubHowItWorksPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="research" />}>
            <Route element={<RequirePermission permission="entity.records.read" />}>
              <Route path="research" element={<ResearchWorkspacePage />} />
            </Route>
          </Route>

          <Route element={<RequirePermission permission="entity.records.read" />}>
            <Route path="whatsapp" element={<WhatsAppWorkspacePage />} />
          </Route>

          {/* Partner Engagement module */}
          <Route path="partner-engagement">
            <Route index element={<PartnerEngagementIndex />} />
            <Route element={<RequireModule moduleKey="partner-engagement" />}>
              <Route path="overview" element={<PartnerOverviewPage />} />
              <Route path="partners" element={<PartnersPage />} />
              <Route path="partners/:id" element={<PartnerDetailPage />} />
            </Route>
            <Route element={<RequireModule moduleKey="crm" />}>
              <Route path="enquiries" element={<EnquiriesPage />} />
              <Route path="enquiries/:id" element={<EnquiryDetailPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="contacts/:id" element={<ContactDetailPage />} />
              <Route path="icloud" element={<IcloudPage />} />
              {RETIRED_CRM_ROUTES.map(route => (
                <Route key={route.from} path={`${route.from.replace('/partner-engagement/', '')}/*`} element={<LegacyRedirect from={route.from} to={route.to} />} />
              ))}
              <Route path="accounts" element={<AccountsPage />} />
              <Route path="accounts/:id" element={<AccountDetailPage />} />
              <Route path="opportunity-hub" element={<OpportunityHubPage />} />
              <Route path="opportunities" element={<OpportunitiesPage />} />
              <Route path="opportunities/:id" element={<OpportunityDetailPage />} />
              <Route path="test-data-review" element={<TestDataReviewPage />} />
            </Route>
          </Route>

          {/* Legacy URLs → Partner Engagement (old bookmarks keep working) */}
          <Route path="crm/*" element={<LegacyRedirect from="/crm" to="/partner-engagement" />} />
          <Route element={<RequireModule moduleKey="crm" />}>
            <Route path="insights" element={<InsightsHubPage />} />
            <Route path="insights/:tab" element={<InsightsHubPage />} />
            <Route path="connect" element={<ConnectHubPage />} />
            <Route path="connect/:tab" element={<ConnectHubPage />} />
          </Route>
          <Route
            path="enquiries/*"
            element={<LegacyRedirect from="/enquiries" to="/partner-engagement/enquiries" />}
          />
          <Route
            path="contacts/*"
            element={<LegacyRedirect from="/contacts" to="/partner-engagement/contacts" />}
          />
          <Route
            path="accounts/*"
            element={<LegacyRedirect from="/accounts" to="/partner-engagement/accounts" />}
          />
          <Route
            path="opportunities/*"
            element={<LegacyRedirect from="/opportunities" to="/partner-engagement/opportunities" />}
          />

          <Route element={<RequirePermission permission="entity.records.write" />}>
            <Route path="manage/quotes" element={<AdminQuotesPage />} />
            <Route path="manage/quotes/new" element={<AdminQuoteDetailPage />} />
            <Route path="manage/quotes/:id" element={<AdminQuoteDetailPage />} />
          </Route>
          <Route element={<RequirePermission permission="entity.records.read" />}>
            <Route path="manage/proposals" element={<TendersPage />} />
            <Route path="manage/tenders" element={<TendersPage />} />
            <Route path="manage/tenders/opportunities" element={<TenderOpportunitiesPage />} />
            <Route path="manage/tenders/opportunities/:id" element={<TenderOpportunityDetailPage />} />
            <Route path="manage/tenders/:id" element={<TenderDetailPage />} />
            <Route path="manage/sows" element={<SowsPage />} />
            <Route path="manage/sows/:id" element={<SowDetailPage />} />
          </Route>

          {/* Administration — presentation gates mirror server permissions. */}
          <Route element={<RequireMicrositeAdmin />}>
            <Route path="manage/microsite" element={<EntityMicrositeAdminPage />} />
          </Route>

          <Route element={<RequirePermission permission="tenant.members.manage" />}>
            <Route path="manage/catalogue" element={<CatalogueAdminPage />} />
            <Route path="manage/services" element={<ServiceCatalogueAdminPage />} />
            <Route path="manage/account-intelligence" element={<AccountIntelligencePage />} />
            <Route path="manage/xero" element={<XeroAdminPage />} />
            <Route path="manage/xero/invoices/:id" element={<XeroInvoiceDetailPage />} />
            <Route path="manage/xero/bank-transactions" element={<XeroBankFeedPage />} />
            <Route
              path="manage/xero/reconciliation"
              element={<XeroReconciliationPage />}
            />
            <Route
              path="manage/accounting/:platform"
              element={<AccountingPlatformPage />}
            />
            <Route path="manage/business-cases" element={<AdminBusinessCasesPage />} />
            <Route path="manage/content" element={<ContentCatalogueAdminPage />} />
            <Route path="manage/events" element={<AdminEventsPage />} />
            <Route path="manage/pages" element={<AdminCustomPagesPage />} />
            <Route path="manage/pages/:id" element={<AdminCustomPageBuilderPage />} />
            <Route path="manage/events/:id" element={<AdminEventDetailPage />} />
            <Route path="manage/legacy-data" element={<LegacyDataPage />} />
            <Route path="manage/legacy-import" element={<LegacyImportPage />} />
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<SettingsGeneralPage />} />
              <Route path="appearance" element={<SettingsAppearancePage />} />
              <Route path="homepage" element={<HomepageConfigurationPage />} />
              <Route path="channels" element={<SettingsChannelsPage />} />
              <Route path="accounting" element={<SettingsAccountingPage />} />
              <Route path="platform" element={<PlatformConfigurationPage />} />
              <Route path="programme-operations" element={<CrmProgrammeOperationsPage />} />
              <Route path="access" element={<AdminUsersPage />} />
              <Route path="users" element={<Navigate to="/settings/access" replace />} />
              <Route path="audit-log" element={<AdminAuditLogPage />} />
            </Route>
          </Route>

          {/* Legacy admin URLs → new locations under Settings */}
          <Route path="admin/users" element={<Navigate to="/settings/access" replace />} />
          <Route path="admin/audit-log" element={<Navigate to="/settings/audit-log" replace />} />
          </Route>
        </Route>
      </Route>
      </Routes>
    </Suspense>
  );
}
